import { format2 } from "../../helpers/helper";
import { useTheme } from "@mui/material/styles";
import React, { useState, useMemo, useEffect } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Typography, IconButton, Skeleton, alpha } from "@mui/material";
import { fetchMonthlySalesByCategoryGroup } from "../../services/orderAnalytics.service";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const QUARTER_MONTHS = {
    1: ["January", "February", "March"],
    2: ["April", "May", "June"],
    3: ["July", "August", "September"],
    4: ["October", "November", "December"],
};

const INITIAL_DATA = MONTHS.map(m => ({ month: m, project: 0, retail: 0 }));
const CHART_HEIGHT = 140; 
const MIN_HEIGHT_FOR_TEXT = 30;

const MonthlySalesChart = ({ dateParams, isDateRangeIncomplete }) => {
    const [quarter, setQuarter] = useState(() => Math.floor((new Date().getMonth() + 3) / 3));
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [yearData, setYearData] = useState(INITIAL_DATA);
    const theme = useTheme();

    const { from, to } = dateParams || {};

    useEffect(() => {
        if (isDateRangeIncomplete || !from || !to) {
            setYearData(INITIAL_DATA);
            setIsLoading(false);
            setError(null);
            return;
        }

        let isMounted = true;

        const loadMonthlySales = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const res = await fetchMonthlySalesByCategoryGroup({ from, to });
                if (!isMounted) return;

                const monthlyMap = {};
                (res || []).forEach(({ month, category, total }) => {
                    if (!monthlyMap[month]) {
                        monthlyMap[month] = { RET: 0, PRJ: 0 };
                    }
                    monthlyMap[month][category] = Number(total) || 0;
                });

                const formattedData = MONTHS.map((label, idx) => ({
                    month: label,
                    project: monthlyMap[idx + 1]?.PRJ || 0,
                    retail: monthlyMap[idx + 1]?.RET || 0,
                }));

                setYearData(formattedData);
            } catch (err) {
                if (isMounted) {
                    console.error("Monthly Sales API failed:", err);
                    setError("Unable to load sales data. Please try again.");
                    setYearData(INITIAL_DATA);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadMonthlySales();

        return () => {
            isMounted = false;
        };
    }, [from, to, isDateRangeIncomplete]);

    const hasNoData = useMemo(
        () => yearData.every(d => d.project === 0 && d.retail === 0),
        [yearData]
    );

    const maxValue = useMemo(() => {
        const highestVal = Math.max(...yearData.map(d => Math.max(d.project, d.retail)), 0);
        return (highestVal || 100) * 1.3;
    }, [yearData]);

    const gridPoints = [0, 0.25, 0.5, 0.75, 1];

    const quarterTotal = useMemo(() => {
        const startIdx = (quarter - 1) * 3;
        return yearData
            .slice(startIdx, startIdx + 3)
            .reduce((sum, d) => sum + d.project + d.retail, 0)
            .toLocaleString();
    }, [quarter, yearData]);

    const tableValues = useMemo(() => {
        const startIdx = (quarter - 1) * 3;
        return yearData.slice(startIdx, startIdx + 3).map((d, i) => ({
            m: QUARTER_MONTHS[quarter][i],
            v: (d.project + d.retail).toLocaleString(),
        }));
    }, [quarter, yearData]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    bgcolor: "background.paper",
                    borderRadius: "20px",
                    p: 2,
                    display: "flex",
                    gap: 3,
                    height: { xs: "auto", sm: 220 },
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    flexDirection: { xs: "column", sm: "row" }
                }}
            >
                <Box sx={{ minWidth: { xs: "100%", sm: 150 } }}>
                    <Skeleton height={24} sx={{ mb: 2, borderRadius: 1 }} />
                    {[1, 2, 3].map(i => (
                        <Skeleton key={i} height={16} sx={{ mb: 1, borderRadius: 1 }} />
                    ))}
                </Box>

                <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 1, height: { xs: 150, sm: "auto" } }}>
                    {Array.from({ length: 12 }).map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            width={12}
                            height={Math.random() * 80 + 40}
                            sx={{ borderRadius: "2px" }}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    if (hasNoData) {
        return (
            <Box
                sx={{
                    height: 220,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No category data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            bgcolor: "background.paper",
            transition: "all 0.2s ease",
            "&:hover": { transform: "translateY(-2px)" },
            borderRadius: "20px",
            p: 2,
            display: "flex",
            gap: { xs: 4, sm: 3 },
            color: "text.primary",
            height: { xs: "auto", sm: 220 },
            minHeight: 220,
            boxShadow: "0px 10px 30px rgba(0,0,0,0.5)",
            flexDirection: { xs: "column", sm: "row" }
        }}>

            <Box sx={{
                minWidth: { xs: "100%", sm: 150 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly"
            }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                    <IconButton
                        disabled={isDateRangeIncomplete || isLoading}
                        onClick={() => setQuarter(q => (q === 1 ? 4 : q - 1))}
                        sx={{ color: "text.secondary", bgcolor: "divider", p: 0.5 }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <Box sx={{ bgcolor: "divider", px: 2, py: 0.3, borderRadius: "20px" }}>
                        <Typography sx={{ color: "secondary.main", fontSize: 12 }}>
                            Quarter {quarter}
                        </Typography>
                    </Box>

                    <IconButton
                        disabled={isDateRangeIncomplete || isLoading}
                        onClick={() => setQuarter(q => (q === 4 ? 1 : q + 1))}
                        sx={{ color: "text.secondary", bgcolor: "divider", p: 0.5 }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ color: "text.secondary", fontSize: 10 }}>MONTH</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 10 }}>VALUE</Typography>
                </Box>

                {tableValues.map((item, i) => (
                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                        <Typography sx={{ color: "text.primary", fontSize: 11 }}>{item.m}</Typography>
                        <Typography sx={{ color: "text.primary", fontSize: 11 }}>{item.v}</Typography>
                    </Box>
                ))}
            </Box>

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: { xs: 180, sm: 'auto' } }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 3, height: 10, bgcolor: "#8695F5", borderRadius: "2px" }} />
                        <Typography sx={{ color: "#8695F5", fontSize: 10 }}>Project</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 3, height: 10, bgcolor: "#07B8B2", borderRadius: "2px" }} />
                        <Typography sx={{ color: "#07B8B2", fontSize: 10 }}>Retail</Typography>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, position: "relative", height: 110 }}>
                    <Box sx={{
                        position: "absolute",
                        width: "100%",
                        height: CHART_HEIGHT,
                        bottom: 20,
                        display: "flex",
                        flexDirection: "column-reverse",
                        justifyContent: "space-between"
                    }}>
                        {gridPoints.map(ratio => {
                            const val = maxValue * ratio;
                            const label =
                                val >= 1_000_000
                                    ? (val / 1_000_000).toFixed(1) + "M"
                                    : val >= 1_000
                                        ? (val / 1_000).toFixed(0) + "K"
                                        : val.toFixed(0);

                            return (
                                <Box key={ratio} sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography sx={{ fontSize: 8, color: "text.primary", width: 35, textAlign: "right", mr: 1 }}>
                                        {label}
                                    </Typography>
                                    <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 20,
                            left: `calc( 35px + ${(quarter - 1) * 3} * ((100% - 35px) / 12) )`,
                            width: `calc( 3 * ((100% - 35px) / 12) * 0.85 )`,
                            height: CHART_HEIGHT,
                            bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                            borderRadius: "8px",
                            zIndex: 0,
                            transform: "translateX(7.5%)",
                            transition: "left 0.45s ease, width 0.3s ease",
                        }}
                    >
                        <Typography sx={{ position: "absolute", top: -15, left: "50%", transition: "top 0.3s ease", transform: "translateX(-50%)", fontSize: 11, fontWeight: 700, color: "secondary.main" }}>
                            {quarterTotal}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 20,
                            left: 35,
                            width: "calc(100% - 35px)",
                            height: CHART_HEIGHT,
                            display: "flex",
                            zIndex: 1,
                            opacity: isDateRangeIncomplete ? 0.4 : 1,
                            pointerEvents: isDateRangeIncomplete ? "none" : "auto",
                        }}
                    >
                        {yearData.map((d, i) => {
                            const pH = Math.min((d.project / maxValue) * 100, 100);
                            const rH = Math.min((d.retail / maxValue) * 100, 100);

                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        flex: 1,
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "flex-end",
                                        gap: 0.5,
                                        position: "relative"
                                    }}
                                >
                                    <Box sx={{
                                        width: { xs: 8, sm: 12 },
                                        height: `${pH}%`,
                                        bgcolor: "#8695F5",
                                        transition: "height 0.6s ease",
                                        borderRadius: "2px 2px 0 0",
                                        position: "relative",
                                        overflow: "visible"
                                    }}>
                                        {d.project > 0 && (
                                            <Typography sx={{
                                                position: "absolute",
                                                fontSize: { xs: 7, sm: 9 },
                                                color:
                                                    pH >= MIN_HEIGHT_FOR_TEXT
                                                        ? "#fff"
                                                        : theme.palette.text.primary,
                                                transform: "rotate(-90deg)",
                                                whiteSpace: "nowrap",
                                                left: "50%",
                                                top: pH >= MIN_HEIGHT_FOR_TEXT ? "50%" : "auto",
                                                bottom: pH >= MIN_HEIGHT_FOR_TEXT ? "auto" : "100%",
                                                transform: pH >= MIN_HEIGHT_FOR_TEXT ? "translate(-50%, -50%) rotate(-90deg)" : "translate(-50%, -15px) rotate(-90deg)"
                                            }}>
                                                {format2(d.project)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        width: { xs: 8, sm: 12 },
                                        height: `${rH}%`,
                                        bgcolor: "#07B8B2",
                                        transition: "height 0.6s ease",
                                        borderRadius: "2px 2px 0 0",
                                        position: "relative",
                                        overflow: "visible"
                                    }}>
                                        {d.retail > 0 && (
                                            <Typography sx={{
                                                position: "absolute",
                                                fontSize: { xs: 7, sm: 9 },
                                                color:
                                                    rH >= MIN_HEIGHT_FOR_TEXT
                                                        ? "#fff"
                                                        : theme.palette.text.primary,
                                                transform: "rotate(-90deg)",
                                                whiteSpace: "nowrap",
                                                left: "50%",
                                                top: rH >= MIN_HEIGHT_FOR_TEXT ? "50%" : "auto",
                                                bottom: rH >= MIN_HEIGHT_FOR_TEXT ? "auto" : "100%",
                                                transform: rH >= MIN_HEIGHT_FOR_TEXT ? "translate(-50%, -50%) rotate(-90deg)" : "translate(-50%, -15px) rotate(-90deg)"
                                            }}>
                                                {format2(d.retail)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography sx={{ position: "absolute", bottom: -16, fontSize: { xs: 7, sm: 8 }, color: "text.secondary" }}>
                                        {d.month}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default React.memo(MonthlySalesChart);